/**
 * Zod schema for the AppointmentForm (B1.6).
 *
 * Kept in a standalone module so it can be imported from:
 *   - AppointmentForm (UI validation)
 *   - Tests (roundtrip / step-gate assertions)
 *   - A future backend contract synchronization guard
 */
import { z } from 'zod';

const HHMM = /^\d{2}:\d{2}$/;
const YYYYMMDD = /^\d{4}-\d{2}-\d{2}$/;

export const appointmentFormSchema = z
  .object({
    customerId: z.string().trim().min(1, 'customerRequired'),
    petId: z.string().trim().min(1, 'petRequired'),
    serviceId: z.string().trim().min(1, 'serviceRequired'),
    staffId: z.string().trim().min(1, 'staffRequired'),
    date: z.string().regex(YYYYMMDD, 'dateRequired'),
    startTime: z.string().regex(HHMM, 'startRequired'),
    endTime: z.string().regex(HHMM, 'endRequired'),
    price: z
      .number({ invalid_type_error: 'priceInvalid' })
      .min(0, 'priceInvalid'),
    currency: z.string().trim().min(1),
    notes: z.string().optional(),
    isVip: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.startTime >= value.endTime) {
      ctx.addIssue({
        path: ['endTime'],
        code: z.ZodIssueCode.custom,
        message: 'endBeforeStart',
      });
    }
  });

export type AppointmentFormValues = z.infer<typeof appointmentFormSchema>;

export const STEP_FIELDS = {
  customer: ['customerId', 'petId'] as const,
  service: ['serviceId', 'staffId'] as const,
  schedule: [
    'date',
    'startTime',
    'endTime',
    'price',
    'currency',
  ] as const,
};

export type AppointmentFormStep = keyof typeof STEP_FIELDS;
export const FORM_STEPS: AppointmentFormStep[] = ['customer', 'service', 'schedule'];
